import { EDITOR_PPM as PPM } from './floorPlan.logic.ts';
import { facadeFromApiRotation } from './draftRotation.logic.ts';
import { isFacilityType, isArchitectureType } from './floorEntities.logic.ts';
import { normalizeRackStatus, normalizeRackRole } from './rackLifecycle.logic.ts';
import { placementFromSource, placementToPersisted } from './placement.logic.ts';
import { createRackChangesOperation } from './draftOperation.logic.ts';

export function mapApiRack(
  rack: any,
  isDraft = false,
  defaultSettings?: { defaultRackWidthM?: number; defaultRackDepthM?: number; defaultRackHeightM?: number }
): any {
  const persistedId = rack.id ?? rack.db_id ?? rack.dbId;
  // Draft palette items historically used 10000+ staging indices. They are not
  // row addresses, even when an old draft also persisted a row_id.
  const isUnassignedDraftRack = isDraft && (
    Number(rack.row_index) >= 10000 || Number(rack.rack_index) >= 10000
  );
  const id = persistedId != null
    ? `rack-db-${persistedId}`
    : isDraft && rack.row_index >= 10000
      ? `dup-${rack.row_index}-${rack.rack_index}`
      : `rack-${rack.row_index}-${rack.rack_index}`;
  const facade = facadeFromApiRotation(rack.rotation);
  const rackType = rack.type || 'server_rack';
  const isFac = rack.is_facility != null ? Boolean(rack.is_facility) : isFacilityType(rackType);
  const isBlk = rack.is_blocked != null ? Boolean(rack.is_blocked) : isArchitectureType(rackType);
  const uSlots = isFac ? 0 : (rack.u_slots ?? null);
  const placement = placementFromSource({
    ...rack,
    width_m: rack.width_m ?? rack.widthM ?? defaultSettings?.defaultRackWidthM,
    depth_m: rack.depth_m ?? rack.depthM ?? defaultSettings?.defaultRackDepthM,
    x: rack.x ?? (rack.rack_index ?? 0) * 60,
    y: rack.y ?? (rack.row_index ?? 0) * 60,
  });
  const pixels = {
    x: Math.round(placement.xM * PPM),
    y: Math.round(placement.yM * PPM),
    width: Math.round(placement.widthM * PPM),
    height: Math.round(placement.depthM * PPM),
  };

  return {
    id,
    ...pixels,
    placement,
    ...facade,
    label: rack.label || (isUnassignedDraftRack ? 'Без адреса' : 'Адрес неизвестен'),
    rackType,
    uCount: uSlots,
    isFacility: isFac,
    isBlocked: isBlk,
    widthM: rack.width_m ?? rack.widthM ?? null,
    depthM: rack.depth_m ?? rack.depthM ?? null,
    heightM: rack.height_m ?? null,
    tenant: rack.tenant ?? null,
    status: normalizeRackStatus(rack.status),
    role: normalizeRackRole(rack.role, rackType),
    rowId: isUnassignedDraftRack ? undefined : rack.row_id,
    moduleId: isUnassignedDraftRack ? undefined : rack.module_id,
    rackPosInRow: isUnassignedDraftRack ? undefined : rack.rack_pos_in_row,
    isDirty: isUnassignedDraftRack,
    rowIndex: rack.row_index ?? 0,
    rackIndex: rack.rack_index ?? 0,
    dbId: persistedId,
    max_power_w: rack.max_power_w ?? null,
    max_weight_kg: rack.max_weight_kg ?? null,
  };
}

export function buildStagedChangesJson(racksRecord: Record<string, any>, baselineRacks: Record<string, any>): any {
  const diff: any = { updated: {}, added: [], deleted: [] };
  const currentDbIds = new Set<number>();

  Object.values(racksRecord).forEach(r => {
    const isFac = r.isFacility != null ? Boolean(r.isFacility) : isFacilityType(r.rackType);
    const isBlk = r.isBlocked != null ? Boolean(r.isBlocked) : isArchitectureType(r.rackType);

    const payload = {
      row_index: r.rowIndex ?? 0,
      rack_index: r.rackIndex ?? 0,
      ...placementToPersisted(r.placement || placementFromSource(r)),
      type: r.rackType,
      u_slots: isFac ? 0 : (r.uCount ?? null),
      is_facility: isFac,
      is_blocked: isBlk,
      label: r.label,
      width_m: r.widthM,
      depth_m: r.depthM,
      height_m: r.heightM,
      tenant: r.tenant,
      status: r.status || 'active',
      role: r.role || normalizeRackRole(r.role, r.rackType),
      // Explicit null means clear membership; undefined disappears during JSON serialization.
      row_id: r.rowId ?? null,
      module_id: r.moduleId ?? null,
      rack_pos_in_row: r.rackPosInRow ?? null,
      max_power_w: r.max_power_w ?? null,
      max_weight_kg: r.max_weight_kg ?? null,
    };

    if (!r.dbId) {
      diff.added.push(payload);
    } else {
      currentDbIds.add(r.dbId);
      const baseline = Object.values(baselineRacks).find(b => b.dbId === r.dbId);
      if (baseline) {
        const isChanged = (
           r.x !== baseline.x || r.y !== baseline.y || r.rotation !== baseline.rotation ||
           r.rowId !== baseline.rowId || r.rackPosInRow !== baseline.rackPosInRow ||
           r.moduleId !== baseline.moduleId ||
           r.tenant !== baseline.tenant || r.rackType !== baseline.rackType ||
           r.label !== baseline.label || r.widthM !== baseline.widthM ||
           r.depthM !== baseline.depthM || r.heightM !== baseline.heightM ||
           r.uCount !== baseline.uCount || r.max_power_w !== baseline.max_power_w ||
           r.max_weight_kg !== baseline.max_weight_kg ||
           r.status !== baseline.status || r.role !== baseline.role
        );
        if (isChanged || r.isDirty) {
           diff.updated[r.dbId.toString()] = payload;
        }
      } else {
        diff.updated[r.dbId.toString()] = payload;
      }
    }
  });

  Object.values(baselineRacks).forEach(b => {
    if (b.dbId && !currentDbIds.has(b.dbId)) {
      diff.deleted.push(b.dbId);
    }
  });

  return {
    ...diff,
    operation: createRackChangesOperation(diff),
  };
}
